import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/frontend/components/ui/sheet";
import { Button } from "@/frontend/components/ui/button";
import { Input } from "@/frontend/components/ui/input";
import { Label } from "@/frontend/components/ui/label";
import { Switch } from "@/frontend/components/ui/switch";
import { orpcClient } from "@/frontend/orpc-client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { orpc } from "@/frontend/orpc-client";
import { HealthSystemChip, HealthSystemSearch } from "../patient/HealthSystemSearch";
import type { HealthSystemOption } from "@/frontend/types/dashboard/health-systems";

/**
 * Right-side panel for adding one patient, their suggested health systems, and
 * optionally their magic link in a single submit.
 *
 * The three writes are sequenced rather than parallel because the recommended
 * sources and the link both need the membership id the first call returns.
 */
export function AddPatientSheet({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (patientMembershipId: string) => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [suggested, setSuggested] = useState<HealthSystemOption[]>([]);
  const [generateLink, setGenerateLink] = useState(true);

  const reset = () => {
    setName("");
    setEmail("");
    setSuggested([]);
    setGenerateLink(true);
  };

  const addPatient = useMutation({
    mutationFn: async (input: { name: string; email: string }) => {
      const patient = await orpcClient.admin.createUser({ ...input, role: "user" });

      for (const source of suggested) {
        await orpcClient.admin.addUserRecommendedSource({
          patientMembershipId: patient.id,
          fhirBaseUrl: source.fhirBaseUrl,
        });
      }

      if (generateLink) {
        await orpcClient.admin.generateMagicLink({ patientMembershipId: patient.id });
      }

      return patient;
    },
    onSuccess: async (patient) => {
      await queryClient.invalidateQueries({ queryKey: orpc.admin.listPatients.key() });
      await queryClient.invalidateQueries({ queryKey: orpc.admin.getPatientListCounts.key() });
      toast.success(generateLink ? "Patient added, link ready to copy" : "Patient added");
      reset();
      onOpenChange(false);
      onCreated(patient.id);
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "Could not add this patient");
    },
  });

  const canSubmit = name.trim().length > 0 && email.trim().length > 0 && !addPatient.isPending;
  const chosenUrls = new Set(suggested.map((source) => source.fhirBaseUrl));

  return (
    // The draft survives an accidental close: state is only cleared after a
    // successful create, so reopening restores whatever was typed.
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full gap-0 sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Add a patient</SheetTitle>
        </SheetHeader>

        <form
          id="add-patient-form"
          className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-4 pb-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (!canSubmit) return;
            addPatient.mutate({ name: name.trim(), email: email.trim() });
          }}
        >
          <div className="flex flex-col gap-2">
            <Label htmlFor="add-patient-name" className="text-sm font-medium">
              Full name
            </Label>
            <Input
              id="add-patient-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Enter the patient's full name"
              className="h-9 text-sm"
              autoFocus
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="add-patient-email" className="text-sm font-medium">
              Email
            </Label>
            <Input
              id="add-patient-email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="name@example.com"
              className="h-9 text-sm"
            />
            <p className="text-xs text-muted-foreground">Email for any communication.</p>
          </div>

          <div className="flex flex-col gap-2">
            <Label className="text-sm font-medium">Suggest health systems</Label>
            {suggested.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {suggested.map((source) => (
                  <HealthSystemChip
                    key={source.fhirBaseUrl}
                    name={source.name}
                    logo={source.logo}
                    onRemove={() =>
                      setSuggested((current) =>
                        current.filter((item) => item.fhirBaseUrl !== source.fhirBaseUrl),
                      )
                    }
                  />
                ))}
              </div>
            )}
            <HealthSystemSearch
              addedUrls={chosenUrls}
              onSelect={(source) => setSuggested((current) => [...current, source])}
            />
            <p className="text-xs text-muted-foreground">
              These appear first when they open their link. They can search for others.
            </p>
          </div>
        </form>

        {/* Pinned above the actions: this card must not move as the result list
            opens and closes above it. */}
        <SheetFooter className="mt-auto flex-col gap-3 border-t">
          <label className="flex w-full items-center gap-3 rounded-lg border p-3">
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium">Generate their magic link now</span>
              <span className="mt-0.5 block text-xs text-muted-foreground">
                You&apos;ll get a link to copy. Otherwise they&apos;re saved and you generate it
                later.
              </span>
            </span>
            <Switch checked={generateLink} onCheckedChange={setGenerateLink} />
          </label>

          <div className="flex w-full justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              className="h-9"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="add-patient-form"
              disabled={!canSubmit}
              className="h-9 gap-1.5"
            >
              {addPatient.isPending && <Loader2 className="size-4 animate-spin" />}
              Add patient
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
